// src/pages/outsourcing_management/requisitions/RequisitionItemLinesEditor.tsx
//
// EE-207 Phase 3 (#10) - dynamic item-lines editor. Infinite lines, fields
// driven by the requisition-level Type (Raw Material vs Tool), profile-driven
// dimension fields (shapeClass, never hardcoded profile names), and a live
// Total Cutting Length / Total Allowance / Total Required Material preview
// that mirrors the backend formula (see requisitionForm.ts).
//
// Cutting Allowance is locked to the value configured in the requisition
// options (RequisitionConfigController) - it is not rendered as an editable
// field, only its product ("Total Allowance") is shown in the preview row.
import { FunctionComponent } from "react";
import {
  Alert,
  Button,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import { RequisitionOptionsType } from "types/RequisitionTypes";
import {
  RequisitionFormItem,
  isMaterialType,
  isPartBasedType,
  REQ_TYPE_FINISH,
  REQ_TYPE_PROCESSED_RAW_MATERIAL,
  REQ_TYPE_TOOL,
  sendsPartFiles,
  SHAPE_FLAT,
  SHAPE_ROUND,
  calculateMaterialPreview,
  createEmptyItem,
  validateItem,
} from "./requisitionForm";

// "Files sent: STEP ✓ PDF ✓" - shown for the types whose enquiry carries the
// part's model and drawing besides the enquiry PDF; nothing for the rest.
// The backend senders decide, this only shows the requester the rule.
const FilesSent: FunctionComponent<{ typeCode: string }> = ({ typeCode }) => {
  if (!sendsPartFiles(typeCode)) return null;
  const mark = <CheckIcon fontSize="small" sx={{ color: "success.main", ml: 0.25 }} />;
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}
    >
      Files sent:
      <span style={{ display: "inline-flex", alignItems: "center", marginLeft: 8 }}>
        STEP {mark}
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", marginLeft: 8 }}>
        PDF {mark}
      </span>
      {typeCode === REQ_TYPE_PROCESSED_RAW_MATERIAL && (
        <span style={{ marginLeft: 8 }}>(parts picked from the order or quote only)</span>
      )}
    </Typography>
  );
};

type PropsType = {
  typeCode: string;
  items: RequisitionFormItem[];
  onChange: (items: RequisitionFormItem[]) => void;
  options?: RequisitionOptionsType;
  showErrors: boolean;
  /**
   * Whether a blank line can be added by hand. Off for part-based types
   * whose purpose requires a part: those lines come from the parts picked
   * on the header, one each, and a blank line would have no part behind it.
   */
  allowAddLine?: boolean;
  /**
   * Overrides the delete icon. The parent uses it to deselect the header
   * part a line was built from - removing only the line would have the
   * header regenerate it.
   */
  onRemoveLine?: (item: RequisitionFormItem) => void;
};

const numberFieldValue = (value: number | undefined) => (value === undefined ? "" : value);

const parseNumber = (raw: string): number | undefined => {
  if (raw === "") return undefined;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const RequisitionItemLinesEditor: FunctionComponent<PropsType> = ({
  typeCode,
  items,
  onChange,
  options,
  showErrors,
  allowAddLine = true,
  onRemoveLine,
}) => {
  const updateItem = (index: number, changes: Partial<RequisitionFormItem>) => {
    const next = items.slice();
    next[index] = { ...next[index], ...changes };
    onChange(next);
  };

  const removeItem = (index: number) => {
    if (onRemoveLine) {
      onRemoveLine(items[index]);
      return;
    }
    onChange(items.filter((_, i) => i !== index));
  };

  const addItem = () => {
    onChange([...items, createEmptyItem(options?.cuttingAllowanceMm)]);
  };

  return (
    <div>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Item Lines
      </Typography>

      {typeCode && <FilesSent typeCode={typeCode} />}

      {items.length === 0 && (
        <Alert severity={showErrors ? "error" : "info"} sx={{ mb: 2 }}>
          At least one item is required.
        </Alert>
      )}

      {items.map((item, index) => {
        const errors = validateItem(typeCode, item, options);
        const materialType = options?.materialTypes.find((m) => m.code === item.materialTypeCode);
        const profile = options?.profileTypes.find((p) => p.code === item.profileTypeCode);
        const preview = calculateMaterialPreview(
          item.partLengthMm,
          item.numberOfParts,
          item.cuttingAllowanceMm
        );

        return (
          <Paper key={item._key} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "0.5rem",
              }}
            >
              <Typography variant="body2" fontWeight="bold">
                Line {index + 1}
              </Typography>
              <IconButton size="small" onClick={() => removeItem(index)} aria-label="Remove line">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>

            {isMaterialType(typeCode) && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Certificate Required"
                    value={
                      item.certificateRequired === undefined
                        ? ""
                        : item.certificateRequired
                        ? "yes"
                        : "no"
                    }
                    onChange={(e) =>
                      updateItem(index, {
                        certificateRequired:
                          e.target.value === "" ? undefined : e.target.value === "yes",
                      })
                    }
                    error={showErrors && !!errors.certificateRequired}
                    helperText={showErrors ? errors.certificateRequired : undefined}
                  >
                    <MenuItem value="">Select...</MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Material Type"
                    value={item.materialTypeCode ?? ""}
                    onChange={(e) =>
                      updateItem(index, { materialTypeCode: e.target.value || undefined })
                    }
                    error={showErrors && !!errors.materialTypeCode}
                    helperText={showErrors ? errors.materialTypeCode : undefined}
                  >
                    <MenuItem value="">Select...</MenuItem>
                    {options?.materialTypes.map((m) => (
                      <MenuItem key={m.code} value={m.code}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {materialType?.requiresDescription && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Material Description"
                      value={item.materialTypeOtherDescription ?? ""}
                      onChange={(e) =>
                        updateItem(index, { materialTypeOtherDescription: e.target.value })
                      }
                      error={showErrors && !!errors.materialTypeOtherDescription}
                      helperText={showErrors ? errors.materialTypeOtherDescription : undefined}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Profile"
                    value={item.profileTypeCode ?? ""}
                    onChange={(e) => {
                      const nextProfile = options?.profileTypes.find(
                        (p) => p.code === e.target.value
                      );
                      // Clear dims that no longer apply when the shape changes.
                      updateItem(index, {
                        profileTypeCode: e.target.value || undefined,
                        diameterMm: nextProfile?.shapeClass === SHAPE_ROUND ? item.diameterMm : undefined,
                        widthMm: nextProfile?.shapeClass === SHAPE_FLAT ? item.widthMm : undefined,
                        heightMm: nextProfile?.shapeClass === SHAPE_FLAT ? item.heightMm : undefined,
                      });
                    }}
                    error={showErrors && !!errors.profileTypeCode}
                    helperText={showErrors ? errors.profileTypeCode : undefined}
                  >
                    <MenuItem value="">Select...</MenuItem>
                    {options?.profileTypes.map((p) => (
                      <MenuItem key={p.code} value={p.code}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {profile?.shapeClass === SHAPE_ROUND && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Diameter (mm)"
                      value={numberFieldValue(item.diameterMm)}
                      onChange={(e) => updateItem(index, { diameterMm: parseNumber(e.target.value) })}
                      error={showErrors && !!errors.diameterMm}
                      helperText={showErrors ? errors.diameterMm : undefined}
                      inputProps={{ min: 0, step: "any" }}
                    />
                  </Grid>
                )}
                {profile?.shapeClass === SHAPE_FLAT && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Width (mm)"
                        value={numberFieldValue(item.widthMm)}
                        onChange={(e) => updateItem(index, { widthMm: parseNumber(e.target.value) })}
                        error={showErrors && !!errors.widthMm}
                        helperText={showErrors ? errors.widthMm : undefined}
                        inputProps={{ min: 0, step: "any" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Height (mm)"
                        value={numberFieldValue(item.heightMm)}
                        onChange={(e) => updateItem(index, { heightMm: parseNumber(e.target.value) })}
                        error={showErrors && !!errors.heightMm}
                        helperText={showErrors ? errors.heightMm : undefined}
                        inputProps={{ min: 0, step: "any" }}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Part Length (mm)"
                    value={numberFieldValue(item.partLengthMm)}
                    onChange={(e) => updateItem(index, { partLengthMm: parseNumber(e.target.value) })}
                    error={showErrors && !!errors.partLengthMm}
                    helperText={showErrors ? errors.partLengthMm : undefined}
                    inputProps={{ min: 0, step: "any" }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="# Parts"
                    value={numberFieldValue(item.numberOfParts)}
                    onChange={(e) =>
                      updateItem(index, { numberOfParts: parseNumber(e.target.value) })
                    }
                    error={showErrors && !!errors.numberOfParts}
                    helperText={showErrors ? errors.numberOfParts : undefined}
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                {/* Cutting Allowance is locked to the configured value and no
                    longer editable (or shown) per line - see the module note.
                    The value still travels with the payload via
                    createEmptyItem(options.cuttingAllowanceMm) / itemFromApi,
                    and its effect stays visible as "Total Allowance" below. */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Notes"
                    value={item.description ?? ""}
                    onChange={(e) => updateItem(index, { description: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap" }}>
                    <Typography variant="body2">
                      <strong>Total Cutting Length:</strong>{" "}
                      {preview.totalCuttingLengthMm.toLocaleString()} mm
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Allowance:</strong> {preview.totalAllowanceMm.toLocaleString()} mm
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Required Material:</strong>{" "}
                      {preview.totalRequiredMaterialMm.toLocaleString()} mm
                    </Typography>
                  </div>
                </Grid>
              </Grid>
            )}

            {typeCode === REQ_TYPE_TOOL && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Tool Name / ID"
                    value={item.toolName ?? ""}
                    onChange={(e) => updateItem(index, { toolName: e.target.value })}
                    error={showErrors && !!errors.toolName}
                    helperText={showErrors ? errors.toolName : undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Category (optional)"
                    value={item.toolCategoryCode ?? ""}
                    onChange={(e) =>
                      updateItem(index, { toolCategoryCode: e.target.value || undefined })
                    }
                  >
                    <MenuItem value="">None</MenuItem>
                    {options?.toolCategories.map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Quantity"
                    value={numberFieldValue(item.quantity)}
                    onChange={(e) => updateItem(index, { quantity: parseNumber(e.target.value) })}
                    error={showErrors && !!errors.quantity}
                    helperText={showErrors ? errors.quantity : undefined}
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    multiline
                    minRows={2}
                    value={item.description ?? ""}
                    onChange={(e) => updateItem(index, { description: e.target.value })}
                  />
                </Grid>
              </Grid>
            )}

            {isPartBasedType(typeCode) && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  {/* The parts are picked on the header for these types (the
                      purpose requires a Sales Order or Quote part) and each
                      line is built from one of them, so the line shows its
                      part rather than asking again. */}
                  <TextField
                    fullWidth
                    size="small"
                    label="Part"
                    value={item.productName ?? "-"}
                    InputProps={{ readOnly: true }}
                    error={showErrors && !!errors.orderItemId}
                    helperText={showErrors ? errors.orderItemId : undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Quantity"
                    value={item.quantity ?? ""}
                    onChange={(e) =>
                      updateItem(index, {
                        quantity: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                    error={showErrors && !!errors.quantity}
                    helperText={showErrors ? errors.quantity : undefined}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    label="Notes"
                    placeholder={
                      typeCode === REQ_TYPE_FINISH
                        ? "Finish and which surfaces"
                        : "Anything the supplier needs beyond the drawing"
                    }
                    value={item.description ?? ""}
                    onChange={(e) => updateItem(index, { description: e.target.value })}
                  />
                </Grid>
              </Grid>
            )}

            {/* No type, or one this form has no fieldset for - a type seeded
                on the backend that the UI does not know about would otherwise
                draw an empty card that saves a blank line. */}
            {!isMaterialType(typeCode) &&
              typeCode !== REQ_TYPE_TOOL &&
              !isPartBasedType(typeCode) && (
                <Typography variant="body2" color="text.secondary">
                  {typeCode
                    ? "This requisition type has no line fields in this form yet."
                    : "Select a Type above to configure this line."}
                </Typography>
              )}
          </Paper>
        );
      })}

      {allowAddLine && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={addItem}
          disabled={!typeCode}
        >
          Add Line
        </Button>
      )}
    </div>
  );
};

export default RequisitionItemLinesEditor;
